import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { bolAPI } from '../utils/api';
import { formatDecimalOrNull, toNumber } from '../utils/format';
import type { BOL, FreightItem } from '../types';

const totalWeight = (freight: FreightItem[]): string | null =>
  formatDecimalOrNull(freight.reduce((sum, f) => sum + toNumber(f.weight), 0));

const BOLPage: React.FC = () => {
  const [bols, setBols] = useState<BOL[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchBOLs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await bolAPI.getBOLs({
        search: { value: searchTerm },
      });
      
      setBols(response.data || []);
    } catch (error) {
      console.error('Error fetching BOLs:', error);
      toast.error('Failed to load BOLs');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  /**
   * Toggle a stop as "next", optimistically.
   *
   * Mobile first: the driver is in a cab, often moving, on a poor connection.
   * The badge flips immediately and the request settles behind it, because
   * waiting on a round trip before acknowledging a tap reads as a dead button
   * and invites a second tap. Failure rolls the row back and says so.
   *
   * Only one stop can be next, so setting one clears the rest locally to match
   * what the server does structurally (drivers.nextLegId is a single value).
   */
  const toggleNextStop = useCallback(async (leg: BOL, makeNext: boolean) => {
    const previous = bols;
    setBols(current => current.map(b => ({
      ...b,
      isNext: makeNext ? b.id === leg.id : (b.id === leg.id ? false : b.isNext),
    })));

    try {
      await bolAPI.setNextStop(leg.id, makeNext);
    } catch {
      setBols(previous);
      toast.error('Could not update your next stop');
    }
  }, [bols]);

  useEffect(() => {
    fetchBOLs();
  }, [fetchBOLs]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    
    const statusLabels: Record<string, string> = {
      pending: 'Pending',
      in_progress: 'In Progress',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-xl font-bold text-gray-900">Bills of Lading</h1>
        <button
          onClick={fetchBOLs}
          disabled={loading}
          className="p-1.5 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 active:bg-blue-100 transition-colors disabled:opacity-40"
          title="Refresh"
        >
          <svg
            className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search BOLs..."
          value={searchTerm}
          onChange={handleSearch}
          className="w-full md:w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* BOL Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : bols.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-500">
            <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-base font-medium">No BOLs found</p>
            <p className="text-xs">You don't have any bills of lading assigned yet.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {bols.map((bol, index) => {
                const showManifestHeader = bol.loadID && (index === 0 || bols[index - 1].loadID !== bol.loadID);

                /*
                 * Stop number counted from the list, not from bl.loadIndex.
                 *
                 * loadIndex is stored inconsistently: the dispatch board writes
                 * it 0-based while the legacy manifest path starts at 1, so
                 * showing it raw gave drivers a "#0" first stop on some
                 * manifests and "#1" on others. It can also be null. The rows
                 * already arrive ordered by loadID then loadIndex, so counting
                 * position within the manifest is both correct and stable
                 * whatever is in the column.
                 */
                let stopNumber = 1;
                for (let i = index - 1; i >= 0 && bols[i].loadID === bol.loadID; i--) stopNumber++;

                // MySQL sends the flag as 0/1.
                const isNext = Number(bol.isNext) === 1; // raw SQL 0/1 may arrive as "0"
                const isHazmat = Number(bol.isDG) === 1;    // same string-boolean trap
                
                return (
                  <React.Fragment key={bol.id}>
                    {/* Manifest Header */}
                    {showManifestHeader && (
                      <div className="bg-blue-50 px-3 py-2 border-b border-blue-200">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm font-semibold text-blue-900">
                            Manifest: {bol.loadName || `Load #${bol.loadID}`}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <Link
                      to={`/bol/${bol.id}`}
                      className={`block transition-colors ${
                        isNext
                          ? 'bg-amber-50 hover:bg-amber-100 active:bg-amber-100 border-l-4 border-amber-500'
                          : 'bg-white hover:bg-gray-50 active:bg-gray-100 border-l-4 border-transparent'
                      }`}
                    >
                      {/* Row 1: BOL# + Status, Customer, Name → Name */}
                      <div className="p-3 pb-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {bol.loadID && (
                              <span className="text-xs font-medium text-blue-600">#{stopNumber}</span>
                            )}
                            <span className="text-xs font-semibold text-gray-900">{bol.bolNumber || bol.idbol || 'N/A'}</span>
                            {isNext && (
                              <span className="text-[10px] font-bold uppercase tracking-wide text-amber-900 bg-amber-400 rounded px-1.5 py-0.5">
                                Next
                              </span>
                            )}
                            {/* A driver needs to know a load is placarded before
                                they open it, not after. */}
                            {isHazmat && (
                              <span
                                className="text-[10px] font-bold uppercase tracking-wide text-white bg-red-600 rounded px-1.5 py-0.5"
                                title="Hazardous materials"
                              >
                                HZ
                              </span>
                            )}
                          </div>
                          {getStatusBadge(bol.status)}
                        </div>
                        {bol.customerName && (
                          <div className="text-xs font-semibold text-indigo-600 truncate mb-0.5">{bol.customerName}</div>
                        )}
                        <div className="text-xs text-gray-900 font-medium truncate">
                          {bol.PickName} → {bol.DropName}
                        </div>
                      </div>

                      {/* Row 2: Customer Ref, MAWB, HAWB */}
                      <div className="px-3 pb-1">
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          {bol.freight && bol.freight.length > 0 && bol.freight[0].customerRef && (
                            <span className="truncate">Ref: {bol.freight[0].customerRef}</span>
                          )}
                          {bol.freight && bol.freight.length > 0 && bol.freight[0].mawb && (
                            <span className="truncate">MAWB: {bol.freight[0].mawb}</span>
                          )}
                          {bol.freight && bol.freight.length > 0 && bol.freight[0].hawb && (
                            <span className="truncate">HAWB: {bol.freight[0].hawb}</span>
                          )}
                        </div>
                      </div>

                      {/* Row 3: Pcs and Wt, and the next-stop control */}
                      <div className="px-3 pb-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-4 text-xs text-gray-600">
                            {bol.freight && bol.freight.length > 0 && (
                              <>
                                <span>Pcs: {bol.freight.reduce((sum, f) => sum + toNumber(f.pieces), 0)}</span>
                                <span>Wt: {totalWeight(bol.freight) ? `${totalWeight(bol.freight)} lbs` : 'N/A'}</span>
                              </>
                            )}
                          </div>

                          {/*
                            Sits inside the row's <Link>, so both handlers are
                            required -- without them a tap navigates to the BOL
                            instead of setting the flag.

                            min-h-[44px] and the generous padding are the point:
                            this is used one-handed, in a cab, often moving.
                          */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              void toggleNextStop(bol, !isNext);
                            }}
                            aria-pressed={isNext}
                            aria-label={isNext ? 'Clear next stop' : 'Set as next stop'}
                            className={`shrink-0 min-h-[44px] px-4 rounded-lg text-xs font-semibold transition-colors ${
                              isNext
                                ? 'bg-amber-500 text-white active:bg-amber-600'
                                : 'bg-gray-100 text-gray-700 active:bg-gray-200'
                            }`}
                          >
                            {isNext ? 'Next ✓' : 'Set next'}
                          </button>
                        </div>
                      </div>
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BOLPage; 